import { useState } from 'react'
import { Sparkles, Loader2, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react'
import api from '../../services/api'

const OVERALL_STYLES = {
  low: 'bg-green-50 border-green-200 text-green-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  high: 'bg-red-50 border-red-200 text-red-700',
}
const SEV_DOT = { low: 'bg-green-400', medium: 'bg-amber-400', high: 'bg-red-500' }

/**
 * AIContractRisk — on-demand risk scan of the contract document before signing.
 * Shown to the consultant (their own contract) and Admin.
 */
export default function AIContractRisk({ contractId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)

  const scan = async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await api.get(`/ai/contract-risk/${contractId}`)
      setData(r.data)
    } catch (e) {
      setError(e.response?.data?.detail || 'Risk scan failed')
    } finally {
      setLoading(false)
    }
  }

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <ShieldAlert className="w-4 h-4 text-indigo-500" />
          <span>Want a second pair of eyes? Let the AI scan this contract for risky or unusual clauses before you sign.</span>
        </div>
        <button
          onClick={scan}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 shrink-0"
        >
          {loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Scanning…</> : <><Sparkles className="w-3 h-3" /> AI Risk Scan</>}
        </button>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    )
  }

  if (data.error || data.available === false) {
    return <p className="text-xs text-gray-400 px-1">AI risk scan unavailable: {data.error || 'no API key configured'}</p>
  }

  return (
    <div className={`rounded-xl border p-4 ${OVERALL_STYLES[data.overall] || OVERALL_STYLES.low}`}>
      <button onClick={() => setExpanded(e => !e)} className="w-full flex items-center gap-2 text-sm font-semibold">
        <Sparkles className="w-4 h-4" />
        AI Risk Scan — Overall risk: {String(data.overall || '').toUpperCase()}
        <span className="ml-auto">{expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>
      {expanded && (
        <div className="mt-2 space-y-2 text-xs">
          <p>{data.summary}</p>
          {data.risks?.length > 0 && (
            <ul className="space-y-1.5 pt-1">
              {data.risks.map((r, i) => (
                <li key={i} className="flex items-start gap-2 bg-white/60 rounded-lg px-2.5 py-1.5">
                  <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${SEV_DOT[r.severity] || 'bg-gray-300'}`} />
                  <span><strong>{r.clause}:</strong> {r.issue}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] opacity-70">Advisory only — review with your administrator if anything is unclear.</p>
        </div>
      )}
    </div>
  )
}
